"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MessageSquare,
  X,
  Lightbulb,
  Bug,
  Palette,
  HelpCircle,
  Sparkles,
} from "lucide-react";
import { FeedbackDialog } from "./feedback-dialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type FeedbackType =
  | "bug"
  | "feature"
  | "design"
  | "improvement"
  | "question"
  | "other";

interface FeedbackWidgetProps {
  enabled?: boolean;
}

export function FeedbackWidget({ enabled = true }: FeedbackWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Check both environment variable and localStorage
    const envEnabled = process.env.NEXT_PUBLIC_ENABLE_FEEDBACK === "true";
    const localEnabled = localStorage.getItem("feedbackEnabled") === "true";
    setIsVisible(enabled && (envEnabled || localEnabled));
  }, [enabled]);

  if (!isVisible || !mounted) return null;

  const feedbackTypes = [
    {
      type: "bug" as FeedbackType,
      icon: Bug,
      label: "Report Bug",
      color: "text-red-500",
    },
    {
      type: "feature" as FeedbackType,
      icon: Lightbulb,
      label: "Request Feature",
      color: "text-yellow-500",
    },
    {
      type: "design" as FeedbackType,
      icon: Palette,
      label: "Design Feedback",
      color: "text-purple-500",
    },
    {
      type: "improvement" as FeedbackType,
      icon: Sparkles,
      label: "Suggest Improvement",
      color: "text-blue-500",
    },
    {
      type: "question" as FeedbackType,
      icon: HelpCircle,
      label: "Ask Question",
      color: "text-green-500",
    },
  ];

  const handleTypeSelect = (type: FeedbackType) => {
    setSelectedType(type);
    setShowMenu(false);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedType(null);
    setShowMenu(false);
  };

  const feedbackButton = (
    <>
      <TooltipProvider>
        {/* Hide button container when panel is open to avoid clipping */}
        {!isOpen && (
          <div
            data-feedback-widget
            className="fixed bottom-2 right-2 z-[9999] flex flex-col items-end gap-2 pointer-events-auto"
          >
            {/* Menu Options */}
            {showMenu && (
              <div className="flex flex-col gap-2 animate-in slide-in-from-bottom-2 duration-200 bg-white/70 p-2 pb-8 -mb-8 rounded-t-full">
                {feedbackTypes.map(({ type, icon: Icon, label, color }) => (
                  <Tooltip key={type}>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="secondary"
                        className={`h-10 w-10 rounded-full shadow-lg hover:shadow-xl transition-all ${color} bg-background/95 backdrop-blur border-2 border-border`}
                        onClick={() => handleTypeSelect(type)}
                      >
                        <Icon className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>{label}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            )}

            {/* Main Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  className={`h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all ${
                    showMenu ? "rotate-45" : ""
                  } bg-gradient-to-br from-purple-950 to-blue-950 hover:from-purple-900 hover:to-blue-900`}
                  onClick={() => setShowMenu(!showMenu)}
                >
                  {showMenu ? (
                    <X className="h-6 w-6 text-white" />
                  ) : (
                    <MessageSquare className="h-6 w-6 text-white" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>{showMenu ? "Close" : "Send Feedback"}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </TooltipProvider>

      {/* Feedback Dialog */}
      {isOpen && selectedType && (
        <FeedbackDialog
          type={selectedType}
          open={isOpen}
          onClose={handleClose}
        />
      )}
    </>
  );

  return createPortal(feedbackButton, document.body);
}
